import DriveFolderTree from '../../../user-management/user-details/drive/drive-folder-tree';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, Dispatch, SetStateAction } from 'react';
import { UserKeys, getDriveFolders } from '../../../../api/users-api';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Messages } from '../../../../utils/message-constants';
import DriveFolderOwnedByMeTree from '../../../user-management/user-details/drive/drive-folder-owned-by-me-tree';
import { DriveSweepFormValues } from './create-drive-sweep-form-page';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface ConfigurationStepProps {
  control: Control<DriveSweepFormValues>;
  errors: FieldErrors<DriveSweepFormValues>;
  setValue: UseFormSetValue<DriveSweepFormValues>;
  watch: UseFormWatch<DriveSweepFormValues>;
  autoCompleteValue: string;
  setAutoCompleteValue: Dispatch<SetStateAction<string>>;
  driveId: string | undefined;
  driveIdLoading: boolean;
  selectedFolder: string;
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  selectedFolderOwnedByMe: string;
  setSelectedFolderOwnedByMe: Dispatch<SetStateAction<string>>;
}

export default function ConfigurationStep({
  control,
  errors,
  setValue,
  watch,
  autoCompleteValue,
  setAutoCompleteValue,
  driveId,
  driveIdLoading,
  selectedFolder,
  setSelectedFolder,
  selectedFolderOwnedByMe,
  setSelectedFolderOwnedByMe,
}: ConfigurationStepProps) {
  useEffect(() => {
    if (autoCompleteValue !== '') {
      setValue('ownerId', autoCompleteValue);
    } else {
      setValue('ownerId', '');
    }
  }, [autoCompleteValue, setValue]);

  useEffect(() => {
    if (selectedFolder !== '') {
      setValue('folderId', selectedFolder);

      if (selectedFolder === driveId) {
        setValue('isMyDrive', true);
      } else {
        setValue('isMyDrive', false);
      }
    }
  }, [driveId, selectedFolder, setValue]);

  // handle owned by me
  useEffect(() => {
    if (selectedFolderOwnedByMe !== '') {
      setValue('folderId', selectedFolderOwnedByMe);
      setValue('isMyDrive', false);
    }
  }, [selectedFolderOwnedByMe, setValue]);

  const folderName = watch('folderName') || '';

  const {
    data: allFoldersForMyDrive,
    fetchNextPage: fetchNextPageForMyDrive,
    isFetchingNextPage: isFetchingNextPageForMyDrive,
    isLoading: isLoadingFolderTreeForMyDrive,
  } = useInfiniteQuery({
    queryKey: [UserKeys.DriveFolders, autoCompleteValue],
    queryFn: ({ pageParam }) => {
      return getDriveFolders(
        autoCompleteValue,
        pageParam ? pageParam : '',
        false,
        false,
        false,
        false,
        'name'
      );
    },
    initialPageParam: driveId,
    getNextPageParam: () => {
      return selectedFolder;
    },
    enabled:
      driveId !== undefined &&
      driveId !== '' &&
      autoCompleteValue !== '' &&
      selectedFolder !== '',
    meta: {
      errorMessage: Messages.Errors.Network.Loading('drive folder data'),
    },
  });

  // Fetch the next page of folders for the My Drive
  useEffect(() => {
    if (
      autoCompleteValue !== '' &&
      selectedFolder !== '' &&
      !isFetchingNextPageForMyDrive &&
      !allFoldersForMyDrive?.pageParams.includes(selectedFolder)
    ) {
      fetchNextPageForMyDrive();
    }
  }, [
    allFoldersForMyDrive?.pageParams,
    fetchNextPageForMyDrive,
    isFetchingNextPageForMyDrive,
    selectedFolder,
    autoCompleteValue,
  ]);

  return (
    <Stack gap={1} sx={{ viewTransitionName: 'steps', p: 1, height: '100%' }}>
      <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
        A Drive Sweep transfers ownership and updates permissions for all
        documents within a selected user's drive folder. The chosen user will
        become the new owner, and any new documents added to that folder will
        automatically inherit these updated permissions. Start by searching for
        a user, then select the folder you wish to sweep. The process will run
        on creation and then every two hours unless disabled.
      </Typography>
      <Stack flexDirection="row" gap={3}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              required
              autoComplete="off"
              inputProps={{
                id: 'name',
              }}
              InputLabelProps={{
                htmlFor: 'name',
              }}
              error={!!errors.name}
              helperText={errors?.name?.message || ' '}
              label="Name"
              sx={{ width: '50%' }}
              value={field.value || ''}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="desc"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'desc',
              }}
              InputLabelProps={{
                htmlFor: 'desc',
              }}
              label="Description"
              sx={{ width: '50%' }}
              value={field.value || ''}
              onChange={field.onChange}
            />
          )}
        />
      </Stack>
      <Stack flexDirection="column" alignItems="start" gap={1}>
        <Stack flexDirection={'row'} width={'100%'} gap={3} alignItems="center">
          <Stack sx={{ width: 350 }}>
            <SharedAutocomplete
              sources={['users']}
              autocompleteValue={autoCompleteValue}
              setAutoCompleteValue={setAutoCompleteValue}
            />
          </Stack>
          {(selectedFolder !== '' || selectedFolderOwnedByMe !== '') &&
          autoCompleteValue !== '' ? (
            <Stack>
              <Stack gap={1} flexDirection="row">
                <Typography>Selected Folder: </Typography>
                <Chip
                  label={selectedFolder === driveId ? 'My Drive' : folderName}
                  size="small"
                  color={
                    selectedFolder === driveId ||
                    selectedFolderOwnedByMe === 'ownedByMe'
                      ? 'default'
                      : 'primary'
                  }
                  disabled={
                    selectedFolder === driveId ||
                    selectedFolderOwnedByMe === 'ownedByMe'
                  }
                />
              </Stack>
            </Stack>
          ) : null}
        </Stack>

        <Stack>
          {!!errors?.folderId && (
            <FormHelperText error>{errors?.folderId.message}</FormHelperText>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          flex: 1,
          flexDirection: 'row',
          overflowY: 'auto',
          width: '100%',
        }}
      >
        {driveIdLoading && <CircularProgress size={20} />}
        {driveId ? (
          <DriveFolderTree
            driveId={driveId}
            userEmail={autoCompleteValue}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            allFolders={allFoldersForMyDrive}
            isLoading={isLoadingFolderTreeForMyDrive}
            displayJustDrive
            setValue={setValue}
          />
        ) : null}
        {autoCompleteValue !== '' && driveId && (
          <DriveFolderOwnedByMeTree
            userEmail={autoCompleteValue}
            selectedFolder={selectedFolderOwnedByMe}
            setSelectedFolder={setSelectedFolderOwnedByMe}
            setValue={setValue}
          />
        )}
      </Box>
    </Stack>
  );
}
